import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAppDispatch } from '../../app/hooks';
import { ClinicSiteItems, ClinicSiteResponse } from '../../types/ClinicSiteType';
import { ContentPopup } from '../../types/CommonType';
import { setContentPopup, setIsOpenPopup } from '../slices/globalSlice';
import MultipleSitesSelection from './MultipleSitesSelection';
import SingleSiteSelection from './SingleSiteSelection';
import { SiteSelectionMode } from '../utils/siteListUtils';
import { maxReasonLength } from '../utils/validationUtils';

interface PopupModalProps {
  isOpen: boolean;
  contentPopup: ContentPopup;
  onClick:
    | ((
        reason?: string,
        selectedSites?: ClinicSiteItems['id'][],
        selectedSite?: ClinicSiteItems['id'] | null
      ) => Promise<void>)
    | (() => Promise<void>);
  onClose?: () => void;
  clinicSites?: ClinicSiteResponse;
}

type PopupErrors = {
  reason?: string;
  selectedSite?: string;
  selectedSites?: string;
};

export default function PopupModal({
  isOpen,
  contentPopup,
  onClick,
  onClose,
  clinicSites,
}: PopupModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>('');
  const [selectedSite, setSelectedSite] = useState<ClinicSiteItems['id'] | null>(null);
  const [selectedSites, setSelectedSites] = useState<ClinicSiteItems['id'][]>([]);
  const [errors, setErrors] = useState<PopupErrors>({});
  const [siteSelectionOpen, setSiteSelectionOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const shouldShowSiteSelection = contentPopup?.showSiteSelection;
  const siteSelectionMode = contentPopup?.siteSelectionMode;

  const filteredSites = useMemo(() => {
    if (clinicSites && contentPopup?.clinicAccount) {
      return clinicSites.clinicSites.filter(
        (site) => site.clinicAccountId === contentPopup.clinicAccount
      );
    }
    return [];
  }, [clinicSites, contentPopup?.clinicAccount]);

  const resetPopupState = () => {
    dispatch(setIsOpenPopup({ value: false }));
    dispatch(setContentPopup({ content: undefined }));

    setOpen(false);
    setReason('');
    setSelectedSite(null);
    setSelectedSites([]);
    setSiteSelectionOpen(false);
    setErrors({});
  };

  const validatePopup = () => {
    const popupErrors: PopupErrors = {};

    const hasReasonField = contentPopup?.showReasonField;
    const hasSiteSelection = contentPopup?.showSiteSelection;

    if (hasReasonField && !reason.trim()) {
      popupErrors.reason = t('error.reasonRequired');
    }

    if(hasReasonField && reason.length > maxReasonLength) {
      popupErrors.reason = t('error.reasonMaxLength');
    }

    if (hasSiteSelection && siteSelectionMode === SiteSelectionMode.SINGLE && !selectedSite) {
      popupErrors.selectedSite = t('error.siteRequired');
    }

    if (hasSiteSelection && siteSelectionMode === SiteSelectionMode.MULTIPLE && selectedSites.length === 0) {
      popupErrors.selectedSites = t('error.sitesRequired');
    }

    setErrors(popupErrors);

    return Object.keys(popupErrors).length === 0;
  };

  const handleClose = (event: SyntheticEvent) => {
    if (['btnClose'].includes(event.currentTarget.id)) {
      resetPopupState();
      if (onClose) {
        onClose();
      }
    }
  };

  const handleOk = async (event: SyntheticEvent) => {
    if (['btnOk'].includes(event.currentTarget.id)) {
      if (!validatePopup()) return;

      const hasReasonField = contentPopup?.showReasonField;
      const hasSiteSelection = contentPopup?.showSiteSelection;

      const reasonValue = hasReasonField ? reason : undefined;

      const selectedSitesValue =
        hasSiteSelection && siteSelectionMode === SiteSelectionMode.MULTIPLE ? selectedSites : undefined;

      const selectedSiteValue =
        hasSiteSelection && siteSelectionMode === SiteSelectionMode.SINGLE ? selectedSite : undefined;
      try {
        setIsSubmitting(true);
        await onClick(reasonValue, selectedSitesValue, selectedSiteValue);
        resetPopupState();
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
  };
  useCallback(() => {
    dispatch(setIsOpenPopup({ value: false }));
  }, [dispatch]);

  useEffect(() => {
    setOpen(Boolean(isOpen));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setOpen(false);
      return;
    }

    const initialSites = contentPopup.userClinicSites ?? [];

    setOpen(true);
    setReason('');
    setErrors({});
    setSiteSelectionOpen(false);

    if (contentPopup.siteSelectionMode === SiteSelectionMode.MULTIPLE && Array.isArray(initialSites)) {
      setSelectedSites(initialSites);
      setSelectedSite(null);
    } else if (contentPopup.siteSelectionMode === SiteSelectionMode.SINGLE) {
      setSelectedSite(initialSites[0] ?? null);
      setSelectedSites([]);
    }
  }, [isOpen, contentPopup?.userClinicSites, contentPopup?.siteSelectionMode]);

  return (
    <Dialog
      open={open || false}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth={contentPopup?.warningTitle && contentPopup?.warningContent ? 'sm' : 'md'}
    >
      <DialogTitle
        sx={{ pt: 6, textAlign: 'center', fontSize: (theme) => theme.typography.h1.fontSize }}
      >
        {contentPopup?.title}
        <IconButton
          id="btnClose"
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {contentPopup?.warningTitle && contentPopup?.warningContent && (
          <DialogContentText>
            <Typography
              component="span"
              sx={(theme) => ({ color: theme.xPalette.warning, fontWeight: 900 })}
            >
              {contentPopup?.warningTitle}:
            </Typography>
            <Typography
              component="span"
              sx={(theme) => ({ color: theme.xPalette.warning, marginLeft: '5px' })}
            >
              {contentPopup?.warningContent}
            </Typography>
          </DialogContentText>
        )}
        <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
          {contentPopup?.description}
        </DialogContentText>
        {contentPopup?.showReasonField && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label={contentPopup.reasonLabel || 'Reason'}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (errors.reason) {
                setErrors((prev) => ({ ...prev, reason: undefined }));
              }
            }}
            error={!!errors.reason}
            helperText={errors.reason}
            sx={{ mt: 2 }}
          />
        )}
        {shouldShowSiteSelection && (
          <>
            {siteSelectionMode === SiteSelectionMode.MULTIPLE && (
              <MultipleSitesSelection<ClinicSiteItems>
                value={selectedSites}
                options={filteredSites}
                label={contentPopup?.siteSelectionLabel || 'Sites'}
                open={siteSelectionOpen}
                onToggle={() => setSiteSelectionOpen((prev) => !prev)}
                onChange={(e) => {
                  setSelectedSites(e);
                  if (errors.selectedSites) {
                    setErrors((prev) => ({ ...prev, selectedSites: undefined }));
                  }
                }}
                placeholder={
                  contentPopup?.siteSelectionLabel
                    ? `Select ${contentPopup.siteSelectionLabel.toLowerCase()}`
                    : 'Select site(s)'
                }
                placement="top"
                containerSx={{
                  flex: 1,
                  mt: 2,
                }}
                error={!!errors.selectedSites}
                helperText={errors.selectedSites}
              />
            )}

            {siteSelectionMode === SiteSelectionMode.SINGLE && (
              <SingleSiteSelection<ClinicSiteItems>
                value={selectedSite}
                options={filteredSites}
                label={contentPopup?.siteSelectionLabel || 'Site'}
                onChange={(siteId) => {
                  setSelectedSite(siteId);
                  if (errors.selectedSite) {
                    setErrors((prev) => ({ ...prev, selectedSite: undefined }));
                  }
                }}
                error={!!errors.selectedSite}
                helperText={errors.selectedSite}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          [theme.breakpoints.down('sm')]: {
            height: '130px',
          },
        })}
      >
        <Link
          id="btnClose"
          component="button"
          sx={(theme) => ({
            fontSize: '14px',
            [theme.breakpoints.down('sm')]: {
              height: '130px',
            },
            textTransform: 'none',
            width: '100%',
            padding: '12px 0px',
            [theme.breakpoints.up('sm')]: {
              width: '20%',
              minWidth: '120px',
            },
            [theme.breakpoints.down('sm')]: {
              position: 'absolute',
              width: '95%',
              marginTop: '60px',
              textAlign: 'center',
            },
          })}
          onClick={handleClose}
        >
          {contentPopup?.btnClose}
        </Link>
        <Button
          id="btnOk"
          variant="contained"
          sx={(theme) => ({
            backgroundColor: '#388AC8',
            textTransform: 'none',
            width: '100%',
            padding: '12px 0px',
            borderRadius: '6px',
            [theme.breakpoints.up('sm')]: {
              width: '20%',
              minWidth: '150px',
            },
            [theme.breakpoints.down('sm')]: {
              position: 'absolute',
              width: '95%',
              marginBottom: '60px',
            },
          })}
          onClick={handleOk}
          disabled={isSubmitting}
        >
          {contentPopup?.btnOk}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
